/**
 * Agent Management Service
 *
 * Service for managing AI agents and configurations.
 */

import api from './api';

const agentManagementService = {
  /**
   * List all available agent templates
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   */
  listAgents: async (params = {}) => {
    try {
      const response = await api.get('/agents', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing agents:', error);
      throw error;
    }
  },

  /**
   * Get details for a specific agent
   * @param {string} agentId - Agent ID
   * @param {boolean} includeProtected - Include protected fields (admin only)
   */
  getAgentDetails: async (agentId, includeProtected = false) => {
    try {
      const response = await api.get(`/agents/${agentId}`, {
        params: { include_protected: includeProtected }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting agent details:', error);
      throw error;
    }
  },

  /**
   * Execute a task using an agent
   * @param {Object} data - Execution data
   * @param {string} data.agent_id - Agent template ID
   * @param {string} data.task - Task description
   * @param {Object} data.context - Additional context (optional)
   * @param {string} data.project_id - Project context (optional)
   * @param {boolean} data.stream - Stream response (optional)
   */
  executeAgentTask: async (data) => {
    try {
      const response = await api.post('/agents/execute', data);
      return response.data;
    } catch (error) {
      console.error('Error executing agent task:', error);
      throw error;
    }
  },

  /**
   * Get agent configuration for current organization
   * @param {string} agentId - Agent ID
   */
  getAgentConfig: async (agentId) => {
    try {
      const response = await api.get(`/agents/${agentId}/config`);
      return response.data;
    } catch (error) {
      console.error('Error getting agent config:', error);
      throw error;
    }
  },

  /**
   * Update agent configuration for current organization
   * @param {string} agentId - Agent ID
   * @param {Object} config - Configuration updates
   * @param {number} config.temperature - Temperature (0.0-2.0) (optional)
   * @param {number} config.max_tokens - Max tokens (100-200000) (optional)
   * @param {string} config.model - Model name (optional)
   */
  updateAgentConfig: async (agentId, config) => {
    try {
      const response = await api.patch(`/agents/${agentId}/config`, config);
      return response.data;
    } catch (error) {
      console.error('Error updating agent config:', error);
      throw error;
    }
  },

  /**
   * Get list of agent categories
   */
  getAgentCategories: async () => {
    try {
      const response = await api.get('/agents/categories');
      return response.data;
    } catch (error) {
      console.error('Error getting agent categories:', error);
      throw error;
    }
  },

  /**
   * Get execution logs for an agent
   * @param {string} agentId - Agent ID
   * @param {number} limit - Max results (1-500, default: 50)
   */
  getAgentExecutionLogs: async (agentId, limit = 50) => {
    try {
      const response = await api.get(`/agents/${agentId}/execution-logs`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting execution logs:', error);
      throw error;
    }
  }
};

export default agentManagementService;
